import {ComponentType} from 'react';
import {Text, Tooltip, VStack} from '@chakra-ui/react';
import {ArrowDownIcon, ArrowUpIcon} from '@chakra-ui/icons';
import {ArticleStatutEvolutionEnum} from 'api-types/ffconnect';
import {formaterPlageJours} from 'ff-common';
import {useTranslation} from 'react-i18next';
import {
  DotIcon,
  HorizontalDashIcon,
} from '@/components/svg/StatutEvolutionIcons';

export interface StatutEvolutionConfig {
  Icon: ComponentType<{boxSize?: string | number; color?: string}>;
  color: string;
  /** Clé i18n du libellé court (ex: « Gagnée ») */
  labelKey: string;
  /** Clé i18n de la phrase métier explicative */
  descriptionKey: string;
}

// Mapping `statut_evolution` → indicateur visuel utilisé en mode commercial.
// eslint-disable-next-line react-refresh/only-export-components -- config partagée avec la barre de filtres tendances du Cadencier
export const STATUT_EVOLUTION_CONFIG: Record<
  ArticleStatutEvolutionEnum,
  StatutEvolutionConfig
> = {
  'en-hausse': {
    Icon: ArrowUpIcon,
    color: 'green.success',
    labelKey: 'historique_livraison.evolution.en-hausse.titre',
    descriptionKey: 'historique_livraison.evolution.en-hausse.description',
  },
  gagne: {
    Icon: DotIcon,
    color: 'green.success',
    labelKey: 'historique_livraison.evolution.gagne.titre',
    descriptionKey: 'historique_livraison.evolution.gagne.description',
  },
  stable: {
    Icon: HorizontalDashIcon,
    color: 'blue.main',
    labelKey: 'historique_livraison.evolution.stable.titre',
    descriptionKey: 'historique_livraison.evolution.stable.description',
  },
  'en-baisse': {
    Icon: ArrowDownIcon,
    color: 'orange.warning',
    labelKey: 'historique_livraison.evolution.en-baisse.titre',
    descriptionKey: 'historique_livraison.evolution.en-baisse.description',
  },
  perdu: {
    Icon: DotIcon,
    color: 'red.warning',
    labelKey: 'historique_livraison.evolution.perdu.titre',
    descriptionKey: 'historique_livraison.evolution.perdu.description',
  },
  'a-gagner': {
    Icon: DotIcon,
    color: 'gray.dark',
    labelKey: 'historique_livraison.evolution.a-gagner.titre',
    descriptionKey: 'historique_livraison.evolution.a-gagner.description',
  },
};

// eslint-disable-next-line react-refresh/only-export-components -- helper partagé avec HistoriqueAchatButton
export const getStatutEvolutionConfig = (
  statut: ArticleStatutEvolutionEnum | undefined,
): StatutEvolutionConfig | undefined => {
  if (!statut) return undefined;
  return STATUT_EVOLUTION_CONFIG[statut];
};

/**
 * Pastille de tendance d'un article (mode commercial) : icône d'évolution +
 * tooltip explicatif du statut. Réutilisée dans l'historique d'achat et la
 * barre de filtres tendances du Cadencier.
 */
export const PastilleEvolution: React.FC<{config: StatutEvolutionConfig}> = ({
  config,
}) => {
  const {t} = useTranslation();
  // Plages de dates des fenêtres servant au calcul du statut d'évolution, affichées dans le tooltip.
  const p1 = formaterPlageJours(30); // 30 derniers jours
  const p2 = formaterPlageJours(60, 30); // les 30 jours d'avant
  const p3 = formaterPlageJours(60); // 60 derniers jours
  return (
    <Tooltip
      hasArrow
      placement='top'
      label={
        <VStack
          alignItems='stretch'
          spacing='1'
          minW='220px'
        >
          <Text
            fontSize='xs'
            fontWeight='700'
          >
            {t(config.labelKey)}
          </Text>
          <Text fontSize='xs'>{t(config.descriptionKey, {p1, p2, p3})}</Text>
        </VStack>
      }
    >
      <config.Icon
        boxSize={3.5}
        color={config.color}
      />
    </Tooltip>
  );
};
